
import { useState, useRef } from 'react';
import { LocalImporter, type ImportResult } from '../services/LocalImporter';

interface Props {
    onClose: () => void;
    onBookImported: (bookId: string) => void;
}

export function ImportModal({ onClose, onBookImported }: Props) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [importedBook, setImportedBook] = useState<ImportResult | null>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setStatus('processing');
        setErrorMsg(null);

        try {
            // Give UI a moment to update
            await new Promise(r => setTimeout(r, 100));
            const result = await LocalImporter.importFile(file);
            setImportedBook(result);
            setStatus('success');
        } catch (err: any) {
            console.error('Import failed', err);
            setErrorMsg(err.message || 'Failed to parse file');
            setStatus('error');
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={onClose}>
            <div style={{
                background: 'var(--surface-color)',
                padding: '24px', borderRadius: '12px',
                width: '90%', maxWidth: '400px',
                display: 'flex', flexDirection: 'column', gap: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-color)'
            }} onClick={e => e.stopPropagation()}>

                <h3 style={{ margin: 0 }}>Import Local Book</h3>

                {status === 'idle' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                            Select an <b>.epub</b> or <b>.html</b> file from your device.
                            It will be processed locally and saved to your library.
                        </p>
                        <input
                            type="file"
                            accept=".epub,.html,.htm,.xhtml,.mht,.mhtml"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <button
                            className="btn-primary"
                            onClick={() => fileInputRef.current?.click()}
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', background: 'var(--accent-color)' }}
                        >
                            Choose File...
                        </button>
                    </div>
                )}

                {status === 'processing' && (
                    <div style={{ textAlign: 'center', padding: '30px' }}>
                        <div className="spinner" style={{ margin: '0 auto 15px' }}>⏳</div>
                        <div>Processing book...</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '5px' }}>Parsing content and extracting metadata</div>
                    </div>
                )}

                {status === 'error' && (
                    <div style={{ textAlign: 'center', color: '#ff6b6b' }}>
                        <p>⚠️ Import Failed</p>
                        <p style={{ fontSize: '0.9rem' }}>{errorMsg}</p>
                        <button
                            onClick={() => setStatus('idle')}
                            style={{ marginTop: '10px', padding: '8px 16px', borderRadius: '4px', background: 'var(--surface-hover)', border: '1px solid var(--border-color)', color: 'var(--text-color)', cursor: 'pointer' }}
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {status === 'success' && importedBook && (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '10px' }}>✅</div>
                        <h4 style={{ margin: '0 0 5px' }}>{importedBook.title}</h4>
                        <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{importedBook.author}</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                onClick={() => onBookImported(importedBook.bookId)}
                                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: 'none', background: '#4CAF50', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                Read Now
                            </button>
                            <button
                                onClick={onClose}
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
